import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';

// ============================================================
// HELPERS
// ============================================================
const fileToDataURL = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const humanSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.round(bytes / 1024)} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

// ============================================================
// TYPES
// ============================================================
interface CategoryRow {
  id: number;
  name: string;
  logo: string | null;
  bg: string | null;
  created_at: string;
  updated_at: string;
}

// ============================================================
// COMPONENT
// ============================================================
const CategoryImageAdmin: React.FC = () => {
  const [name, setName] = useState('');
  const [logo, setLogo] = useState<string>(''); // base64 data URI
  const [bg, setBg] = useState<string>('');     // base64 data URI

  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ---------------- Load ----------------
  const loadCategories = async () => {
    setListLoading(true);
    const { data, error: sbError } = await supabase
      .from('vendimgcat')
      .select('*')
      .order('id', { ascending: false });

    if (sbError) setError('Failed to load: ' + sbError.message);
    else setCategories(data || []);
    setListLoading(false);
  };

  useEffect(() => {
    loadCategories();
  }, []);

  // ---------------- Pick + convert to base64 immediately ----------------
  const handlePick = async (
    e: React.ChangeEvent<HTMLInputElement>,
    kind: 'logo' | 'bg'
  ) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    try {
      const dataURL = await fileToDataURL(file);
      if (kind === 'logo') setLogo(dataURL);
      else setBg(dataURL);
      setError('');
      setSuccess('');
    } catch {
      setError('Failed to read image.');
    }
  };

  const trigger = (id: string) => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (el) el.click();
  };

  // ---------------- Submit — insert base64 straight into the row ----------------
  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!logo) {
      setError('Logo is required.');
      return;
    }

    setLoading(true);
    try {
      const { error: sbError } = await supabase
        .from('vendimgcat')
        .upsert(
          {
            name,
            logo,               // full data URI, e.g. data:image/png;base64,iVBOR...
            bg: bg || null,     // null if not provided
            updated_at: new Date().toISOString()
          },
          { onConflict: 'name' }
        );

      if (sbError) throw sbError;

      setSuccess('Category saved.');
      setName('');
      setLogo('');
      setBg('');
      await loadCategories();
    } catch (err: any) {
      setError(err?.message || 'Failed to save.');
    } finally {
      setLoading(false);
    }
  };

  // ---------------- Delete ----------------
  const handleDelete = async (categoryName: string) => {
    setDeleting(true);
    setError('');
    try {
      const { error: sbError } = await supabase
        .from('vendimgcat')
        .delete()
        .eq('name', categoryName);

      if (sbError) throw sbError;

      setConfirmDelete(null);
      await loadCategories();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete.');
    } finally {
      setDeleting(false);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F4F1E8] to-[#efe9d8] py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#9B4819] text-center mb-2">
          Category Image Admin
        </h1>
        <p className="text-center text-sm text-gray-600 mb-8">
          Logo &amp; background stored as base64 directly in <code>vendimgcat</code>.
        </p>

        {/* ---------- FORM ---------- */}
        <div className="bg-white rounded-3xl shadow-xl border border-black/5 p-6 sm:p-8 mb-8">
          {/* Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. restaurant"
              className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#9B4819] focus:ring-2 focus:ring-[#9B4819]/15 outline-none text-base"
            />
          </div>

          {/* Image pickers */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">
                  Logo <span className="text-red-500">*</span>
                </p>
                {logo && (
                  <button
                    type="button"
                    onClick={() => setLogo('')}
                    className="text-xs text-red-600 hover:text-red-800"
                  >
                    Clear
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => trigger('logo-input')}
                className="w-full aspect-square rounded-2xl border-2 border-dashed border-gray-200 hover:border-[#9B4819]/50 bg-gray-50 overflow-hidden flex items-center justify-center transition"
              >
                {logo ? (
                  <img src={logo} alt="Logo preview" className="w-full h-full object-contain p-2" />
                ) : (
                  <span className="text-xs text-gray-500">Tap to upload logo</span>
                )}
              </button>
              <input
                id="logo-input"
                type="file"
                accept="image/*"
                onChange={(e) => handlePick(e, 'logo')}
                className="hidden"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">Background</p>
                {bg && (
                  <button
                    type="button"
                    onClick={() => setBg('')}
                    className="text-xs text-red-600 hover:text-red-800"
                  >
                    Clear
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => trigger('bg-input')}
                className="w-full aspect-square rounded-2xl border-2 border-dashed border-gray-200 hover:border-[#9B4819]/50 bg-gray-50 overflow-hidden flex items-center justify-center transition"
              >
                {bg ? (
                  <img src={bg} alt="BG preview" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs text-gray-500">Tap to upload background</span>
                )}
              </button>
              <input
                id="bg-input"
                type="file"
                accept="image/*"
                onChange={(e) => handlePick(e, 'bg')}
                className="hidden"
              />
            </div>
          </div>

          {/* Feedback */}
          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700 mb-4 whitespace-pre-wrap">
              {error}
            </div>
          )}
          {success && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-700 mb-4">
              {success}
            </div>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-3.5 rounded-xl text-sm font-semibold text-white bg-[#9B4819] hover:bg-[#7a3914] transition disabled:opacity-40 flex items-center justify-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Saving...
              </>
            ) : (
              'Save Category'
            )}
          </button>
        </div>

        {/* ---------- LIST ---------- */}
        <div className="bg-white rounded-3xl shadow-xl border border-black/5 p-6 sm:p-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Existing categories ({categories.length})
          </h2>

          {listLoading ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : categories.length === 0 ? (
            <p className="text-sm text-gray-500">No categories yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {categories.map((cat) => (
                <li key={cat.id} className="py-3 flex items-center gap-4">
                  {cat.logo && (
                    <img
                      src={cat.logo}
                      alt="category"
                      className="w-12 h-12 rounded-lg object-contain bg-gray-50 border border-gray-100 p-1 shrink-0"
                    />
                  )}
                  {cat.bg && (
                    <img
                      src={cat.bg}
                      alt="background"
                      className="w-16 h-12 rounded-lg object-cover border border-gray-100 shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {cat.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {cat.logo ? `${humanSize(cat.logo.length)} logo` : 'No logo'} ·{' '}
                      {cat.bg ? `${humanSize(cat.bg.length)} bg` : 'No BG'}
                    </p>
                  </div>

                  {confirmDelete === cat.name ? (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleDelete(cat.name)}
                        disabled={deleting}
                        className="text-xs font-semibold text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-lg transition disabled:opacity-50"
                      >
                        {deleting ? '...' : 'Yes, delete'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(null)}
                        disabled={deleting}
                        className="text-xs font-medium text-gray-600 hover:text-gray-800 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(cat.name)}
                      className="text-xs font-medium text-red-600 hover:text-red-800 px-3 py-1.5 rounded-lg hover:bg-red-50 transition shrink-0"
                    >
                      Delete
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryImageAdmin;